import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import api, { ApiResponse } from '../axios';
import { ENDPOINTS } from '../endpoints';
import {
  LoginRequest,
  LoginResponse,
  AuthMeResponse,
} from '../types/auth.types';
import { useAuthStore } from '@/stores/authStore';
import { useTenantStore } from '@/stores/tenantStore';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

// Login mutation
export function useLogin() {
  const { login } = useAuthStore();
  const { setTenant } = useTenantStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: LoginRequest) => {
      const response = await api.post<ApiResponse<LoginResponse>>(
        ENDPOINTS.AUTH.LOGIN,
        data
      );
      return response.data.data!;
    },
    onSuccess: (data) => {
      login(data.user, data.accessToken, data.refreshToken);
      // Store tenant info for multi-tenant requests
      if (data.tenant) {
        const features = data.tenant.features || {};
        const enabledFeatures = (Object.keys(features) as (keyof typeof features)[])
          .filter(k => typeof features[k] === 'boolean' && features[k])
          .map(k => String(k));
        setTenant({
          id: data.tenant.id,
          name: data.tenant.name,
          identifier: data.tenant.subdomain,
          features: enabledFeatures,
        });
      }
      toast.success(`שלום, ${data.user.fullName}!`);
      navigate('/dashboard');
    },
    onError: () => {
      toast.error('שם משתמש או סיסמה שגויים');
    },
  });
}

// Logout mutation
export function useLogout() {
  const { logout } = useAuthStore();
  const { clearTenant } = useTenantStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      await api.post(ENDPOINTS.AUTH.LOGOUT);
    },
    onSuccess: () => {
      logout();
      clearTenant();
      toast.success('להתראות!');
      navigate('/login');
    },
    onSettled: () => {
      // Always logout locally even if API fails
      logout();
      clearTenant();
    },
  });
}

// Get current user
export function useCurrentUser() {
  const { setUser, setLoading, token } = useAuthStore();

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const response = await api.get<ApiResponse<AuthMeResponse>>(
        ENDPOINTS.AUTH.ME
      );
      return response.data.data!;
    },
    enabled: !!token,
  });

  // Handle success/error with useEffect (React Query v5 pattern)
  useEffect(() => {
    if (query.isSuccess && query.data) {
      setUser(query.data.user);
      setLoading(false);
    }
  }, [query.isSuccess, query.data, setUser, setLoading]);

  useEffect(() => {
    if (query.isError) {
      setLoading(false);
    }
  }, [query.isError, setLoading]);

  return query;
}

// Forgot password
export function useForgotPassword() {
  return useMutation({
    mutationFn: async (email: string) => {
      await api.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
    },
    onSuccess: () => {
      toast.success('קישור לאיפוס סיסמה נשלח למייל');
    },
    onError: () => {
      toast.error('שגיאה בשליחת המייל');
    },
  });
}

// Reset password
export function useResetPassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: { email: string; token: string; newPassword: string }) => {
      await api.post(ENDPOINTS.AUTH.RESET_PASSWORD, data);
    },
    onSuccess: () => {
      toast.success('הסיסמה אופסה בהצלחה');
      navigate('/login');
    },
    onError: () => {
      toast.error('שגיאה באיפוס הסיסמה. ייתכן שהקישור פג תוקף.');
    },
  });
}
